import { useEffect, useState } from 'react';

import { logger } from '../../../../common/utils/logger';

import { getConfluencePageContributors } from './api/get-confluence-page-contributors';
import { getConfluencePageOutgoingLinks } from './api/get-confluence-page-outgoing-links';
import type { ConfluencePageContributor } from './gql/types';

export const useTwgSources = ({
	workspaceId,
	cloudId,
	product,
	contentId,
}: {
	workspaceId: string;
	cloudId: string;
	product: string;
	contentId: string;
}) => {
	const [loading, setIsLoading] = useState(true);
	const [error, setError] = useState(false);
	const [contributors, setContributors] = useState<ConfluencePageContributor[]>([]);
	const [outgoingLinks, setOutgoingLinks] = useState<string[]>([]);

	useEffect(() => {
		const getLinkedResults = async () => {
			await Promise.allSettled([
				getConfluencePageContributors({
					id: contentId,
					workspaceId,
					cloudId,
					product,
				}),
				getConfluencePageOutgoingLinks({
					id: contentId,
					workspaceId,
					cloudId,
					product,
				}),
			])
				.then(([contributorsResult, outgoingLinksResult]) => {
					setContributors(
						contributorsResult.status === 'fulfilled' ? contributorsResult.value || [] : [],
					);
					setOutgoingLinks(
						outgoingLinksResult.status === 'fulfilled' ? outgoingLinksResult.value || [] : [],
					);

					if (
						contributorsResult.status === 'rejected' &&
						outgoingLinksResult.status === 'rejected'
					) {
						logger.error({
							error:
								contributorsResult.reason ||
								outgoingLinksResult.reason ||
								new Error('Failed to fetch contributors and outgoing links'),
							functionName: 'useTwgSources',
						});
						setError(true);
					}
				})
				.catch((error) => {
					logger.error({ error, functionName: 'useTwgSources' });
					setError(true);
				})
				.finally(() => {
					setIsLoading(false);
				});
		};

		if (Boolean(contentId)) {
			setIsLoading(true);
			getLinkedResults();
		}
	}, [cloudId, contentId, workspaceId, product]);

	return {
		loading,
		error,
		contributors,
		outgoingLinks,
	};
};
